import { useState, useRef, useCallback } from 'react'
import mongoose from 'mongoose'
import { useWebSocketContext } from '@/contexts/WebSocketContext'

export default function useKagamiBridge() {
    const [task, setTask] = useState(null)
    const [taskStatus, setTaskStatus] = useState('')
    const [taskState, setTaskState] = useState('')
    const unsubscribeRef = useRef(null)
    const { addListener } = useWebSocketContext()

    const getToken = () => {
        return window.document.cookie.split('; ').find(row => row.startsWith('auth='))?.split('=')[1]
    }

    const cleanup = useCallback(() => {
        if (unsubscribeRef.current) {
            unsubscribeRef.current()
            unsubscribeRef.current = null
        }
    }, [])
    
    const getTask = async (taskId) => {
        const token = getToken()
        if (!token) {
            throw new Error('Auth cookie not found')
        }
    
        const res = await fetch(`https://0pq3edg3z8.execute-api.us-east-2.amazonaws.com/prod/tasks/${taskId}`,{
            method:'GET',
            headers:{
                'Authorization': `Bearer ${token}`
            }
        })
    
        if(!res.ok){
            throw new Error('Failed to get task')
        }

        const task = await res.json()
    
        setTask(task)
        setTaskStatus(task.status)
        setTaskState(task.state)

        return task
    }
    
    const runTask = async (data = {}) => {
        const token = getToken()
        if (!token) {
            throw new Error('Auth cookie not found')
        }

        if(!data.taskId){
            data.taskId = (new mongoose.Types.ObjectId()).toString()
        }

        setTaskStatus('Submitting task')

        const res = await fetch(`https://0pq3edg3z8.execute-api.us-east-2.amazonaws.com/prod/schedule`,{
            method:'POST',
            headers:{
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(data)
        })
    
        if(!res.ok){
            throw new Error('Failed to run task')
        }

        const task = await res.json()

        setTask(task)
        setTaskStatus(task.status || 'pending')
        setTaskState(task.state)
    
        return task
    }

    const cancelTask = async (taskId) => {
        const token = getToken()
        if (!token) {
            throw new Error('Auth cookie not found')
        }

        const res = await fetch(`https://0pq3edg3z8.execute-api.us-east-2.amazonaws.com/prod/tasks/${taskId}/cancel`,{
            method:'POST',
            headers:{
                'Authorization': `Bearer ${token}`
            },
        })

        if(!res.ok){
            throw new Error('Failed to cancel task')
        }

        return true
    }

    const deleteTask = async (taskId) => {
        const token = getToken()
        if (!token) {
            throw new Error('Auth cookie not found')
        }

        const res = await fetch(`https://0pq3edg3z8.execute-api.us-east-2.amazonaws.com/prod/tasks/${taskId}`,{
            method:'DELETE',
            headers:{
                'Authorization': `Bearer ${token}`
            },
        })

        if(!res.ok){
            throw new Error('Failed to delete task')
        }

        return true
    }
    
    const runTaskAndPoll = async (data) => {
        cleanup()

        const submittedTask = await runTask(data)

        const TASK_EVENTS = ['task_updated', 'task_completed', 'task_failed']

        return new Promise((resolve, reject) => {
            unsubscribeRef.current = addListener((message) => {
                if (!TASK_EVENTS.includes(message?.event)) return
                if (message.data?.taskId !== submittedTask._id) return

                const update = message.data
                setTaskStatus(update.status)
                setTaskState(update.state)
                setTask(prev => ({ ...prev, ...update }))

                if (message.event === 'task_completed' || update.state === 'completed') {
                    cleanup()
                    setTaskStatus('')
                    resolve({ ...submittedTask, ...update })
                } else if (message.event === 'task_failed' || update.state === 'failed') {
                    cleanup()
                    reject(new Error(`${update.state}: ${update.status}`))
                }
            })
        })
    }

    return {
        task,
        taskStatus,
        taskState,
        getTask,
        runTask,
        runTaskAndPoll,
        cancelTask,
        deleteTask
    }
}
